import React, { type FC } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import { useRouteUrl, useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { HOME } from '@confluence/named-routes/entry-points/routes.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';

import { EditIconComponent } from '../GlobalNavigationIcons.tsx';
import { i18n } from '../globalNavigationTranslations.ts';

import { useGlobalItemVisibility } from './useGlobalItemVisibility.ts';
import type { GlobalItemProps } from './globalItemProps.ts';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks.ts'))
			.fireDraftsClickedAnalytics,
);

const onDraftSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl === HOME.toUrl({ view: 'drafts' });
	},
};

export const DraftsItem: FC<GlobalItemProps> = ({ isHidden, peekingId, setPeekingId }) => {
	const routeDataRef = useRouteDataRef();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);

	const isSelected = useRouteUrl(onDraftSelector);

	const shouldHide = useGlobalItemVisibility(
		'drafts',
		isSelected,
		isHidden,
		peekingId,
		setPeekingId,
	);

	if (shouldHide) {
		return null;
	}

	return (
		<LinkMenuItem
			href={HOME.toUrl({ view: 'drafts' })}
			elemBefore={EditIconComponent}
			isSelected={isSelected}
			onClick={fireClickAnalytics}
		>
			<FormattedMessage {...i18n.drafts} />
		</LinkMenuItem>
	);
};
